import axios, { type AxiosInstance } from "axios";
import {
  type AiAgentAuthorizedContact,
  type AiAgentChartData,
  type AiAgentChatMessageNote,
  type AiAgentChatMessageNoteCategory,
  type AiAgentCompanyPrompt,
  type AiAgentConversationDetails,
  type AiAgentFreeRecommendedPrompts,
  type AiAgentHistory,
  type AiAgentRecommendedPromptCategory,
  type AiAgentRecommendedPromptPayload,
  type AiAgentSavedPrompt,
  type LiveChatFile,
} from "./types";

export default class AiAgentApi {
  private readonly api: AxiosInstance;

  constructor(token: string) {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_FACILE_ANA_AGENT,
      headers: { Authorization: token },
    });
  }

  async getHistory() {
    try {
      const { data }: { data: AiAgentHistory[] } = await this.api.get("/history");
      return data;
    } catch (error) {
      console.error("Erro ao tentar obter o histórico de conversas com o agente de IA!\n" + error);
      return [];
    }
  }

  async getHistoryInternal(filters: {
    companyCode: string | null;
    tool: string | null;
    startDate: string | null;
    endDate: string | null;
  }) {
    try {
      const { data }: { data: AiAgentHistory[] } = await this.api.get(`/history/internal`, { params: filters });
      return data;
    } catch (error) {
      console.error("Erro ao tentar obter o histórico de conversas com o agente de IA!\n" + error);
      return [];
    }
  }

  async getHistoryInternalByCompanyCode(companyCode: string) {
    try {
      const { data }: { data: AiAgentHistory[] } = await this.api.get(`/history/internal/${companyCode}`);
      return data;
    } catch (error) {
      console.error("Erro ao tentar obter o histórico de conversas com o agente de IA!\n" + error);
      return [];
    }
  }

  async getConversationDetails(id: string) {
    try {
      const { data }: { data: AiAgentConversationDetails[] } = await this.api.get(`/history/${id}/details`);
      return data;
    } catch (error) {
      console.error("Erro ao tentar obter os detalhes da conversa com o agente de IA!\n" + error);
      return [];
    }
  }

  async inactivateConversation(id: string) {
    try {
      const { status } = await this.api.delete(`/history/${id}`);
      return status === 200;
    } catch (error) {
      console.error("Erro ao tentar deletar a conversa com o agente de IA!\n" + error);
      return false;
    }
  }

  async getTokensThisMonth() {
    try {
      const { data }: { data: { bySystem: number; byContacts: number; total: number; tokensLimit: number } } =
        await this.api.get("/company-token/month-usage");
      return data;
    } catch (error) {
      console.error(
        "Erro ao tentar obter a quantidade total de tokens utilizados pela empresa no mês atual!\n" + error,
      );
      return {
        bySystem: 0,
        byContacts: 0,
        total: 0,
        tokensLimit: 0,
      };
    }
  }

  async getChartData(startDate: string, endDate: string) {
    try {
      const { data }: { data: AiAgentChartData[] } = await this.api.get("/company-token/chart", {
        params: { startDate, endDate },
      });
      return data;
    } catch (error) {
      console.error("Erro ao tentar obter os dados para o gráfico!\n" + error);
      return [];
    }
  }

  async getAuthorizedContacts() {
    try {
      const { data }: { data: AiAgentAuthorizedContact[] } = await this.api.get("/authorized-contact");
      return data;
    } catch (error) {
      console.error(
        "Erro ao tentar obter os contatos autorizados para acessar o agente de IA através do WhatsApp!\n" + error,
      );
      return [];
    }
  }

  async deleteAuthorizedContacts(id: string) {
    try {
      const { status } = await this.api.delete(`/authorized-contact/${id}`);
      return status === 200;
    } catch (error) {
      console.error("Erro ao tentar deletar o contato autorizado da empresa!\n" + error);
      return false;
    }
  }

  async sendMessageFeedback(id: string, feedback: boolean) {
    try {
      const { status } = await this.api.put(`/history/message-feedback/${id}`, { feedback });
      return status === 200;
    } catch (error) {
      console.error("Erro ao tentar enviar o feedback sobre a mensagem!\n" + error);
      return false;
    }
  }

  async getCompanyPrompts() {
    try {
      const { data }: { data: { companyPrompts: AiAgentCompanyPrompt[] } } = await this.api.get(`/company-prompts`);
      return data.companyPrompts;
    } catch (_) {
      return [];
    }
  }

  async upsertCompanyPrompt(content: string) {
    try {
      const { status, data }: { status: number; data: AiAgentCompanyPrompt } = await this.api.put(`/company-prompts`, {
        content,
      });
      return status === 200 && data ? data : null;
    } catch (_) {
      return null;
    }
  }

  async getRecommendedPrompts() {
    try {
      const { data }: { data: AiAgentRecommendedPromptPayload[] } = await this.api.get("/recommended-prompts");
      return data;
    } catch (_) {
      return [];
    }
  }

  async getRecommendedPromptsForUsers() {
    try {
      const { data }: { data: { list: AiAgentSavedPrompt[]; access: boolean } } =
        await this.api.get("/recommended-prompts/users");
      return data;
    } catch (_) {
      return { list: [], access: false };
    }
  }

  async getFreeRecommendedPrompts() {
    try {
      const { data }: { data: AiAgentFreeRecommendedPrompts } = await this.api.get("/recommended-prompts/free");
      return data;
    } catch (_) {
      return { prompts: [] };
    }
  }

  async createRecommendedPrompt(form: {
    title: string;
    categoryId: number;
    content: string;
    active: boolean;
    icon: string | null;
  }) {
    try {
      const { status, data }: { status: number; data: AiAgentRecommendedPromptPayload } = await this.api.post(
        "/recommended-prompts",
        { ...form },
      );
      return status === 201 && data ? data : null;
    } catch (_) {
      return null;
    }
  }

  async updateRecommendedPrompt(
    id: string,
    form: {
      title: string;
      categoryId: number;
      content: string;
      active: boolean;
      icon: string | null;
    },
  ) {
    try {
      const { status, data }: { status: number; data: AiAgentRecommendedPromptPayload } = await this.api.put(
        `/recommended-prompts/${id}`,
        { ...form },
      );
      return status === 200 && data ? data : null;
    } catch (_) {
      return null;
    }
  }

  async deleteRecommendedPrompt(id: string) {
    try {
      const { status }: { status: number } = await this.api.delete(`/recommended-prompts/${id}`);
      return status === 200;
    } catch (_) {
      return false;
    }
  }

  async getRecommendedPromptsCategories() {
    try {
      const { data }: { data: AiAgentRecommendedPromptCategory[] } = await this.api.get(
        "/recommended-prompts/categories",
      );
      return data;
    } catch (_) {
      return [];
    }
  }

  async createRecommendedPromptsCategory(description: string) {
    try {
      const { status, data }: { status: number; data: AiAgentRecommendedPromptCategory } = await this.api.post(
        "/recommended-prompts/categories",
        { description },
      );
      return status === 201 && data ? data : null;
    } catch (_) {
      return null;
    }
  }

  async createMessageNote(messageId: string, category: number, content: string) {
    try {
      const { status, data }: { status: number; data: AiAgentChatMessageNote } = await this.api.post("/message-notes", {
        messageId,
        category,
        content,
      });
      return status === 201 && data ? data : null;
    } catch (_) {
      return null;
    }
  }

  async getConversationsWithNotes(
    period: { startDate: string; endDate: string } | null,
    category: AiAgentChatMessageNoteCategory | null,
  ) {
    try {
      const { data }: { data: AiAgentHistory[] } = await this.api.get("/message-notes", {
        params: {
          ...(period && { startDate: period.startDate, endDate: period.endDate }),
          ...(category && { category }),
        },
      });
      return data;
    } catch (_) {
      return [];
    }
  }

  async uploadLiveChatFile(formData: FormData) {
    try {
      const { data } = await this.api.post<LiveChatFile>(`customer-service/send-attachments`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data;
    } catch (err) {
      console.error(err);
      return null;
    }
  }
}
